import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  CheckCircle2,
  FileText,
  Calculator,
  Clock,
  Phone,
  CreditCard,
  Briefcase,
  Landmark,
  ArrowRight,
  MessageCircle,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"

const WHATSAPP_NUMBER = import.meta.env.VITE_WHATSAPP_NUMBER ?? "18092468383"
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Hola, quiero información sobre financiamiento de vehículos."
)
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`
const PHONE_DISPLAY = "(809) 246-8383"
const PHONE_HREF = "tel:+18092468383"

interface Step {
  icon: LucideIcon
  title: string
  description: string
}

interface RequirementGroup {
  icon: LucideIcon
  title: string
  items: string[]
}

interface Faq {
  question: string
  answer: string
}

const steps: Step[] = [
  {
    icon: FileText,
    title: "Selecciona tu Vehículo",
    description:
      "Explora nuestro catálogo y elige el vehículo que mejor se adapte a tus necesidades.",
  },
  {
    icon: Calculator,
    title: "Calcula tu Cuota",
    description:
      "Utiliza nuestra calculadora para simular diferentes escenarios de financiamiento.",
  },
  {
    icon: CheckCircle2,
    title: "Solicita tu Préstamo",
    description:
      "Completa el formulario de solicitud y adjunta los documentos requeridos.",
  },
  {
    icon: Clock,
    title: "Aprobación",
    description:
      "Recibirás una respuesta en 24-48 horas sobre el estado de tu solicitud.",
  },
]

const requirements: RequirementGroup[] = [
  {
    icon: CreditCard,
    title: "Documentos de Identidad",
    items: [
      "Cédula de identidad y electoral",
      "Comprobante de domicilio reciente",
      "Referencias personales y comerciales",
    ],
  },
  {
    icon: Briefcase,
    title: "Documentos Laborales",
    items: [
      "Carta de trabajo (si es empleado)",
      "Comprobantes de ingresos últimos 3 meses",
      "Declaración de impuestos (si es independiente)",
    ],
  },
  {
    icon: Landmark,
    title: "Documentos Financieros",
    items: [
      "Estados de cuenta bancarios (últimos 3 meses)",
      "Historial crediticio favorable",
      "Comprobante de otros ingresos (si aplica)",
    ],
  },
]

const faqs: Faq[] = [
  {
    question: "¿Cuál es el monto mínimo de inicial requerido?",
    answer:
      "El monto mínimo de inicial es del 20% del valor del vehículo. Sin embargo, mientras mayor sea el inicial, mejores serán las condiciones del financiamiento.",
  },
  {
    question: "¿Cuál es el plazo máximo de financiamiento?",
    answer:
      "Ofrecemos plazos flexibles de hasta 60 meses (5 años), dependiendo del modelo del vehículo y el perfil crediticio del cliente.",
  },
  {
    question: "¿Necesito tener historial crediticio?",
    answer:
      "Aunque es preferible tener un historial crediticio, evaluamos cada caso de manera individual. Nuevos clientes pueden calificar con un inicial mayor o un co-deudor.",
  },
  {
    question: "¿Cuánto tiempo toma el proceso de aprobación?",
    answer:
      "El proceso de pre-aprobación toma 24-48 horas. La aprobación final puede tomar hasta 5 días hábiles dependiendo de la verificación de documentos.",
  },
  {
    question: "¿Puedo realizar pagos anticipados?",
    answer:
      "Sí, aceptamos pagos anticipados sin penalidad. Estos pueden aplicarse para reducir el plazo o la cuota mensual, según prefiera el cliente.",
  },
]

export default function Financing() {
  return (
    <>
      <Helmet>
        <title>Financiamiento de Vehículos | Oriental Ramirez</title>
        <meta
          name="description"
          content="Opciones flexibles de financiamiento para motos y vehículos. Conoce nuestros requisitos y proceso de aprobación rápido. Oriental Ramirez República Dominicana."
        />
        <meta
          name="keywords"
          content="financiamiento vehículos, préstamos motos, crédito vehicular, Oriental Ramirez, República Dominicana"
        />
        <link rel="canonical" href="https://orientalramirez.com/financiamiento" />
      </Helmet>

      <div className="bg-slate-50">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#1F2240] text-white">
          <div
            className="absolute inset-0 opacity-90"
            style={{
              background:
                "linear-gradient(135deg, #1F2240 0%, #39367D 55%, #E10F18 140%)",
            }}
            aria-hidden="true"
          />
          <div
            className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#FFD44D]/20 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#FFD44D] ring-1 ring-white/15">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FFD44D]" />
                Aprobación en 24-48 horas
              </span>
              <h1 className="mt-5 text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
                Financiamiento de Vehículos
              </h1>
              <p className="mt-4 text-base text-slate-200 sm:text-lg">
                Hacemos realidad tu sueño de tener un vehículo nuevo con opciones
                de financiamiento flexibles y adaptadas a tus necesidades.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#E10F18] text-white hover:bg-[#E10F18]/90"
                >
                  <Link to="/catalogo">
                    Ver catálogo
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white"
                >
                  <a href={PHONE_HREF}>
                    <Phone className="h-4 w-4" />
                    {PHONE_DISPLAY}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-[#1F2240] sm:text-3xl">
              Proceso de Financiamiento
            </h2>
            <p className="mt-3 text-sm text-slate-600 sm:text-base">
              Cuatro pasos sencillos para conseguir tu vehículo.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => {
              const Icon = step.icon
              return (
                <Card
                  key={step.title}
                  className="group relative overflow-hidden border-slate-200 transition-all hover:-translate-y-1 hover:border-[#39367D]/40 hover:shadow-lg"
                >
                  <CardContent className="p-6">
                    <span className="absolute right-4 top-3 text-5xl font-extrabold text-slate-100 transition-colors group-hover:text-[#FFD44D]/40">
                      {index + 1}
                    </span>
                    <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#39367D]/10 text-[#39367D]">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="relative mt-4 text-base font-semibold text-[#1F2240]">
                      {step.title}
                    </h3>
                    <p className="relative mt-2 text-sm text-slate-600">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </section>

        {/* Requirements */}
        <section className="bg-white py-14 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl font-bold text-[#1F2240] sm:text-3xl">
                Requisitos
              </h2>
              <p className="mt-3 text-sm text-slate-600 sm:text-base">
                Reúne estos documentos para agilizar tu solicitud.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {requirements.map((group) => {
                const Icon = group.icon
                return (
                  <Card
                    key={group.title}
                    className="border-slate-200 transition-shadow hover:shadow-md"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E10F18]/10 text-[#E10F18]">
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="text-base font-semibold text-[#1F2240]">
                          {group.title}
                        </h3>
                      </div>
                      <ul className="mt-4 space-y-2.5">
                        {group.items.map((item) => (
                          <li
                            key={item}
                            className="flex items-start gap-2.5 text-sm text-slate-600"
                          >
                            <CheckCircle2
                              className="mt-0.5 h-4 w-4 shrink-0 text-[#39367D]"
                              aria-hidden="true"
                            />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-[#1F2240] sm:text-3xl">
                Preguntas Frecuentes
              </h2>
              <p className="mt-3 text-sm text-slate-600 sm:text-base">
                Resolvemos las dudas más comunes sobre el financiamiento.
              </p>
            </div>
            <Card className="mt-8 border-slate-200">
              <CardContent className="p-2 sm:p-4">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={faq.question} value={`item-${index}`}>
                      <AccordionTrigger className="text-left text-sm font-semibold text-[#1F2240] sm:text-base">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Contact / CTA */}
        <section className="relative overflow-hidden bg-[#1F2240] text-white">
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(120deg, #39367D 0%, #1F2240 60%)",
            }}
            aria-hidden="true"
          />
          <div className="relative mx-auto max-w-7xl px-4 py-14 text-center sm:px-6 sm:py-20 lg:px-8">
            <h2 className="text-2xl font-bold sm:text-3xl">
              ¿Listo para dar el siguiente paso?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-200 sm:text-base">
              Nuestro equipo de asesores financieros está disponible para
              responder todas tus dudas y acompañarte en todo el proceso.
            </p>
            <div className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:max-w-none sm:flex-row sm:justify-center">
              <Button
                asChild
                size="lg"
                className="bg-[#E10F18] text-white hover:bg-[#E10F18]/90"
              >
                <Link to="/catalogo">
                  Ver catálogo
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="bg-[#25D366] text-white hover:bg-[#25D366]/90"
              >
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Escríbenos por WhatsApp sobre financiamiento"
                >
                  <MessageCircle className="h-4 w-4" />
                  Escríbenos por WhatsApp
                </a>
              </Button>
            </div>
            <div className="mx-auto mt-8 inline-flex items-center gap-2.5 rounded-full bg-white/10 px-4 py-2 text-sm ring-1 ring-white/15">
              <Phone className="h-4 w-4 text-[#FFD44D]" aria-hidden="true" />
              <span className="text-slate-200">Llámanos:</span>
              <a href={PHONE_HREF} className="font-semibold hover:underline">
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
